/**
 * Attribute Registry — single source of truth for accessibility attributes.
 *
 * All 21 boolean attributes, 5 groups with metadata, dietary options,
 * admin guidance text, and a unified DisplayTag model.
 */

import type { POIAccessibilityAttributes, DietaryOption } from "./types";

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

export interface AccessibilityAttributeDef {
  key: string;
  label: string;
  i18nKey: string;
  groupId: string;
}

export interface AccessibilityGroupDef {
  id: string;
  label: string;
  i18nKey: string;
  icon: string;
  color: string;
  attributes: AccessibilityAttributeDef[];
}

export interface DietaryOptionDef {
  key: DietaryOption;
  label: string;
  i18nKey: string;
}

/** Unified tag model for rendering both custom tags and accessibility tags */
export interface DisplayTag {
  type: "custom" | "accessibility" | "dietary";
  key: string;
  label: string;
  color?: string;
  groupId?: string;
}

// ---------------------------------------------------------------------------
// Group definitions (icon names match FontAwesome identifiers)
// ---------------------------------------------------------------------------

export const ATTRIBUTE_GROUPS: AccessibilityGroupDef[] = [
  {
    id: "mobility",
    label: "Mobility",
    i18nKey: "accessibility.groupMobility",
    icon: "wheelchair",
    color: "#3b82f6",
    attributes: [
      { key: "wheelchair_accessible", label: "Wheelchair accessible", i18nKey: "accessibility.wheelchair_accessible", groupId: "mobility" },
      { key: "wheelchair_restroom", label: "Wheelchair-accessible restroom", i18nKey: "accessibility.wheelchair_restroom", groupId: "mobility" },
      { key: "wheelchair_rental", label: "Wheelchair rental", i18nKey: "accessibility.wheelchair_rental", groupId: "mobility" },
      { key: "scooter_rental", label: "Scooter rental", i18nKey: "accessibility.scooter_rental", groupId: "mobility" },
      { key: "ramp_available", label: "Ramp available", i18nKey: "accessibility.ramp_available", groupId: "mobility" },
      { key: "wide_entrance", label: "Wide entrance", i18nKey: "accessibility.wide_entrance", groupId: "mobility" },
      { key: "elevator", label: "Elevator", i18nKey: "accessibility.elevator", groupId: "mobility" },
      { key: "rest_area", label: "Rest area", i18nKey: "accessibility.rest_area", groupId: "mobility" },
    ],
  },
  {
    id: "sensory",
    label: "Sensory",
    i18nKey: "accessibility.groupSensory",
    icon: "ear-deaf",
    color: "#ec4899",
    attributes: [
      { key: "quiet_zone", label: "Quiet zone", i18nKey: "accessibility.quiet_zone", groupId: "sensory" },
      { key: "sensory_break_room", label: "Sensory break room", i18nKey: "accessibility.sensory_break_room", groupId: "sensory" },
      { key: "hearing_loop", label: "Hearing loop", i18nKey: "accessibility.hearing_loop", groupId: "sensory" },
      { key: "braille_signage", label: "Braille signage", i18nKey: "accessibility.braille_signage", groupId: "sensory" },
    ],
  },
  {
    id: "family",
    label: "Family",
    i18nKey: "accessibility.groupFamily",
    icon: "baby",
    color: "#f59e0b",
    attributes: [
      { key: "changing_station", label: "Changing station", i18nKey: "accessibility.changing_station", groupId: "family" },
      { key: "nursing_room", label: "Nursing room", i18nKey: "accessibility.nursing_room", groupId: "family" },
      { key: "family_restroom", label: "Family restroom", i18nKey: "accessibility.family_restroom", groupId: "family" },
      { key: "stroller_parking", label: "Stroller parking", i18nKey: "accessibility.stroller_parking", groupId: "family" },
      { key: "kid_friendly", label: "Kid friendly", i18nKey: "accessibility.kid_friendly", groupId: "family" },
    ],
  },
  {
    id: "service_animals",
    label: "Service Animals",
    i18nKey: "accessibility.groupServiceAnimals",
    icon: "dog",
    color: "#10b981",
    attributes: [
      { key: "service_animal_relief", label: "Service animal relief", i18nKey: "accessibility.service_animal_relief", groupId: "service_animals" },
      { key: "pet_water_station", label: "Pet water station", i18nKey: "accessibility.pet_water_station", groupId: "service_animals" },
    ],
  },
  {
    id: "environment",
    label: "Environment",
    i18nKey: "accessibility.groupEnvironment",
    icon: "umbrella-beach",
    color: "#64748b",
    attributes: [
      { key: "shaded_area", label: "Shaded area", i18nKey: "accessibility.shaded_area", groupId: "environment" },
      { key: "covered_area", label: "Covered area", i18nKey: "accessibility.covered_area", groupId: "environment" },
    ],
  },
];

// ---------------------------------------------------------------------------
// Flat registry + derived exports
// ---------------------------------------------------------------------------

/** Flat lookup: key → attribute definition */
export const ATTRIBUTE_REGISTRY: Record<string, AccessibilityAttributeDef> = {};
for (const group of ATTRIBUTE_GROUPS) {
  for (const attr of group.attributes) {
    ATTRIBUTE_REGISTRY[attr.key] = attr;
  }
}

/** key → human label (backward compat for API consumers) */
export const ACCESSIBILITY_LABELS: Record<string, string> = {};
for (const [key, def] of Object.entries(ATTRIBUTE_REGISTRY)) {
  ACCESSIBILITY_LABELS[key] = def.label;
}

/** Ordered list of all boolean attribute keys */
export const ACCESSIBILITY_BOOLEAN_KEYS: (keyof POIAccessibilityAttributes)[] =
  ATTRIBUTE_GROUPS.flatMap((g) => g.attributes.map((a) => a.key)) as (keyof POIAccessibilityAttributes)[];

// ---------------------------------------------------------------------------
// Dietary options
// ---------------------------------------------------------------------------

export const DIETARY_OPTION_DEFS: DietaryOptionDef[] = [
  { key: "vegetarian", label: "Vegetarian", i18nKey: "accessibility.dietary.vegetarian" },
  { key: "vegan", label: "Vegan", i18nKey: "accessibility.dietary.vegan" },
  { key: "gluten_free", label: "Gluten-free", i18nKey: "accessibility.dietary.gluten_free" },
  { key: "kosher", label: "Kosher", i18nKey: "accessibility.dietary.kosher" },
  { key: "halal", label: "Halal", i18nKey: "accessibility.dietary.halal" },
];

// ---------------------------------------------------------------------------
// Admin guidance text (tooltip descriptions)
// ---------------------------------------------------------------------------

export const ATTRIBUTE_GUIDANCE: Record<string, string> = {
  wheelchair_accessible:
    "Step-free access, ADA width (36\"+ doorways). Includes wheelchair-accessible viewing areas, seating, entrances.",
  wheelchair_restroom:
    "Restroom with wheelchair access, grab bars, and adequate turning space.",
  wheelchair_rental:
    "Manual wheelchairs or ECVs available for rent on-site.",
  scooter_rental:
    "Electric scooters or ECVs available for mobility-impaired visitors.",
  ramp_available:
    "Ramp access provided for wheelchair/stroller entry.",
  wide_entrance:
    "Entrance is 36+ inches wide for wheelchair/stroller access.",
  elevator:
    "Elevator access to multiple floors or levels.",
  rest_area:
    "Designated seating area for rest breaks. Shaded if outdoor.",
  quiet_zone:
    "Designated low-noise area. No music, announcements, or crowds.",
  sensory_break_room:
    "Enclosed room for visitors who need a break from sensory stimulation. Dim lighting, quiet.",
  hearing_loop:
    "Induction loop system for hearing aids.",
  braille_signage:
    "Signage includes Braille text for visually impaired visitors.",
  changing_station:
    "Baby/infant changing table available.",
  nursing_room:
    "Private space for nursing mothers with seating and outlets.",
  family_restroom:
    "Single-occupancy restroom suitable for families.",
  stroller_parking:
    "Designated parking area for strollers.",
  kid_friendly:
    "Activities, seating, or content designed for children.",
  service_animal_relief:
    "Designated outdoor relief area for service animals.",
  pet_water_station:
    "Water available for service animals or pets.",
  shaded_area:
    "Natural or structural shade available.",
  covered_area:
    "Covered/indoor area protecting from rain or sun.",
};

// ---------------------------------------------------------------------------
// DisplayTag extraction
// ---------------------------------------------------------------------------

/** Group color lookup */
const GROUP_COLORS: Record<string, string> = {};
for (const g of ATTRIBUTE_GROUPS) {
  GROUP_COLORS[g.id] = g.color;
}

/**
 * Extract DisplayTag[] from a POI's accessibility attributes.
 * Handles both boolean attributes and dietary_options array.
 */
export function extractAccessibilityDisplayTags(
  a11y: POIAccessibilityAttributes | Record<string, unknown> | null | undefined,
): DisplayTag[] {
  if (!a11y) return [];
  const result: DisplayTag[] = [];

  // Boolean attributes
  for (const [key, def] of Object.entries(ATTRIBUTE_REGISTRY)) {
    if ((a11y as Record<string, unknown>)[key] === true) {
      result.push({
        type: "accessibility",
        key,
        label: def.label,
        color: GROUP_COLORS[def.groupId],
        groupId: def.groupId,
      });
    }
  }

  // Dietary options
  const dietary = (a11y as Record<string, unknown>).dietary_options;
  if (Array.isArray(dietary)) {
    for (const opt of dietary) {
      const def = DIETARY_OPTION_DEFS.find((d) => d.key === opt);
      if (def) {
        result.push({
          type: "dietary",
          key: def.key,
          label: def.label,
          color: "#22c55e",
          groupId: "dietary",
        });
      }
    }
  }

  return result;
}
